"use client";

import { ExternalLink } from "lucide-react";
import type { EvidenceLabelResolver } from "@/lib/aicheck/evidence-labeler";
import { hasProjectDataReference } from "@/lib/aicheck/project-data-reference";
import type { Evidence } from "@/types/aicheck";

const PROJECT_DATA_REFERENCE_BUTTON_BASE_CLASS =
  "inline-flex shrink-0 items-center gap-1 rounded-md border border-blue-200 bg-blue-50 px-1.5 text-[11px] font-semibold text-blue-700 transition-colors hover:border-blue-300 hover:bg-blue-100";

export function ProjectDataReferenceButton({
  source,
  onOpen,
  className = "",
}: {
  source: string;
  onOpen: (source: string) => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={() => onOpen(source)}
      className={`${PROJECT_DATA_REFERENCE_BUTTON_BASE_CLASS} ${className}`}
      title="Open project data overview at this referenced item"
    >
      <ExternalLink size={11} aria-hidden="true" />
      View data
    </button>
  );
}

export function EvidenceList({
  evidence,
  showHeader = true,
  itemIdPrefix,
  itemIndexOffset = 0,
  highlightedEvidenceIndex,
  labelResolver,
  onProjectDataReferenceClick,
}: {
  evidence: Evidence[];
  showHeader?: boolean;
  itemIdPrefix?: string;
  itemIndexOffset?: number;
  highlightedEvidenceIndex?: number | null;
  labelResolver?: EvidenceLabelResolver;
  onProjectDataReferenceClick?: (source: string) => void;
}) {
  if (!evidence || evidence.length === 0) return null;
  return (
    <div className="space-y-1.5">
      {showHeader && (
        <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">
          Evidence ({evidence.length})
        </div>
      )}
      {evidence.map((e, i) => {
        const formattedSource = labelResolver?.formatSource(e.source) ?? {
          display: e.source,
          raw: null,
        };
        const formattedQuote = labelResolver?.formatQuote(e.quote, e.source);
        const canOpenProjectData =
          onProjectDataReferenceClick && hasProjectDataReference(e.source);

        return (
          <div
            key={i}
            id={
              itemIdPrefix
                ? `${itemIdPrefix}-${itemIndexOffset + i}`
                : undefined
            }
            className={`rounded border bg-white/80 px-2.5 py-1.5 text-xs min-w-0 transition-colors ${
              highlightedEvidenceIndex === itemIndexOffset + i
                ? "border-amber-300 ring-2 ring-amber-300 shadow-sm"
                : "border-gray-200"
            }`}
          >
            <div className="flex items-start justify-between gap-2 text-gray-400">
              <div className="flex min-w-0 items-start gap-1.5">
                <svg
                  viewBox="0 0 16 16"
                  fill="currentColor"
                  className="w-3 h-3 shrink-0 text-gray-300 mt-0.5"
                  aria-hidden="true"
                >
                  <path d="M1.75 1.5a.25.25 0 0 0-.25.25v12.5c0 .138.112.25.25.25h12.5a.25.25 0 0 0 .25-.25V1.75a.25.25 0 0 0-.25-.25H1.75zM0 1.75C0 .784.784 0 1.75 0h12.5C15.216 0 16 .784 16 1.75v12.5A1.75 1.75 0 0 1 14.25 16H1.75A1.75 1.75 0 0 1 0 14.25V1.75zm9.22 3.72a.749.749 0 0 1 1.06 0l3.5 3.5a.749.749 0 0 1 0 1.06l-3.5 3.5a.749.749 0 1 1-1.06-1.06L12.19 9.5H3.75a.75.75 0 0 1 0-1.5h8.44L9.22 5.03a.749.749 0 0 1 0-1.06z" />
                </svg>
                <div className="min-w-0">
                  <div className="text-gray-600 break-words leading-tight">
                    {formattedSource.display}
                  </div>
                  {formattedSource.raw && (
                    <div className="mt-0.5 font-mono text-[11px] text-gray-400 break-all leading-tight">
                      {formattedSource.raw}
                    </div>
                  )}
                </div>
              </div>
              {canOpenProjectData && (
                <ProjectDataReferenceButton
                  source={e.source}
                  onOpen={onProjectDataReferenceClick}
                  className="py-0.5"
                />
              )}
            </div>
            {e.quote && (
              <p className="mt-1 text-gray-600 italic break-words whitespace-pre-wrap leading-relaxed pl-[18px]">
                &ldquo;{formattedQuote ?? e.quote}&rdquo;
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
